/** Softball-specific enums + label maps. Numeric values start at 1 (or 0 for `Unknown`). */

export enum FieldPosition {
  Pitcher = 1,
  Catcher,
  FirstBase,
  SecondBase,
  ThirdBase,
  Shortstop,
  LeftField,
  CenterField,
  RightField,
  DesignatedHitter,
  ExtraHitter,
  Bench
}

export const FieldPositionLabel: Record<FieldPosition, string> = {
  [FieldPosition.Pitcher]: 'Pitcher',
  [FieldPosition.Catcher]: 'Catcher',
  [FieldPosition.FirstBase]: 'First base',
  [FieldPosition.SecondBase]: 'Second base',
  [FieldPosition.ThirdBase]: 'Third base',
  [FieldPosition.Shortstop]: 'Shortstop',
  [FieldPosition.LeftField]: 'Left field',
  [FieldPosition.CenterField]: 'Center field',
  [FieldPosition.RightField]: 'Right field',
  [FieldPosition.DesignatedHitter]: 'Designated hitter',
  [FieldPosition.ExtraHitter]: 'DP/Flex (Extra hitter)',
  [FieldPosition.Bench]: 'Bench'
};

export const FieldPositionShort: Record<FieldPosition, string> = {
  [FieldPosition.Pitcher]: 'P',
  [FieldPosition.Catcher]: 'C',
  [FieldPosition.FirstBase]: '1B',
  [FieldPosition.SecondBase]: '2B',
  [FieldPosition.ThirdBase]: '3B',
  [FieldPosition.Shortstop]: 'SS',
  [FieldPosition.LeftField]: 'LF',
  [FieldPosition.CenterField]: 'CF',
  [FieldPosition.RightField]: 'RF',
  [FieldPosition.DesignatedHitter]: 'DH',
  [FieldPosition.ExtraHitter]: 'EH',
  [FieldPosition.Bench]: 'BN'
};

export enum BattingPosition {
  First = 1,
  Second,
  Third,
  Fourth,
  Fifth,
  Sixth,
  Seventh,
  Eighth,
  Ninth,
  DesignatedHitter,
  ExtraHitter
}

export const BattingPositionLabel: Record<BattingPosition, string> = {
  [BattingPosition.First]: '1st',
  [BattingPosition.Second]: '2nd',
  [BattingPosition.Third]: '3rd',
  [BattingPosition.Fourth]: '4th',
  [BattingPosition.Fifth]: '5th',
  [BattingPosition.Sixth]: '6th',
  [BattingPosition.Seventh]: '7th',
  [BattingPosition.Eighth]: '8th',
  [BattingPosition.Ninth]: '9th',
  [BattingPosition.DesignatedHitter]: 'DH',
  [BattingPosition.ExtraHitter]: 'EH'
};

export enum LineupSlotKind {
  Starter = 1,
  Sub,
  Courtesy,
  Pinch
}

export const LineupSlotKindLabel: Record<LineupSlotKind, string> = {
  [LineupSlotKind.Starter]: 'Starter',
  [LineupSlotKind.Sub]: 'Sub',
  [LineupSlotKind.Courtesy]: 'Courtesy runner',
  [LineupSlotKind.Pinch]: 'Pinch hitter'
};

export enum PitchType {
  Unknown = 0,
  Fastball = 1,
  Curveball,
  ChangeUp,
  Riseball,
  Dropball,
  Screwball,
  Knuckleball
}

export const PitchTypeLabel: Record<PitchType, string> = {
  [PitchType.Unknown]: 'Unknown',
  [PitchType.Fastball]: 'Fastball',
  [PitchType.Curveball]: 'Curveball',
  [PitchType.ChangeUp]: 'Change-up',
  [PitchType.Riseball]: 'Riseball',
  [PitchType.Dropball]: 'Dropball',
  [PitchType.Screwball]: 'Screwball',
  [PitchType.Knuckleball]: 'Knuckleball'
};

export enum PitchResult {
  Ball = 1,
  StrikeSwinging,
  StrikeLooking,
  Foul,
  InPlay,
  HitByPitch
}

export const PitchResultLabel: Record<PitchResult, string> = {
  [PitchResult.Ball]: 'Ball',
  [PitchResult.StrikeSwinging]: 'Strike (swinging)',
  [PitchResult.StrikeLooking]: 'Strike (looking)',
  [PitchResult.Foul]: 'Foul',
  [PitchResult.InPlay]: 'In play',
  [PitchResult.HitByPitch]: 'Hit by pitch'
};

export enum AtBatResult {
  Single = 1,
  Double,
  Triple,
  HomeRun,
  Walk,
  Strikeout,
  Out,
  FieldersChoice,
  Error,
  Sacrifice,
  HitByPitch
}

export const AtBatResultLabel: Record<AtBatResult, string> = {
  [AtBatResult.Single]: 'Single',
  [AtBatResult.Double]: 'Double',
  [AtBatResult.Triple]: 'Triple',
  [AtBatResult.HomeRun]: 'Home run',
  [AtBatResult.Walk]: 'Walk',
  [AtBatResult.Strikeout]: 'Strikeout',
  [AtBatResult.Out]: 'Out',
  [AtBatResult.FieldersChoice]: "Fielder's choice",
  [AtBatResult.Error]: 'Error',
  [AtBatResult.Sacrifice]: 'Sacrifice',
  [AtBatResult.HitByPitch]: 'Hit by pitch'
};

export enum GameInningHalf {
  Top = 1,
  Bottom
}

export const GameInningHalfLabel: Record<GameInningHalf, string> = {
  [GameInningHalf.Top]: 'Top',
  [GameInningHalf.Bottom]: 'Bottom'
};

export enum PitcherAgeBand {
  Age7_8 = 1,
  Age9_10,
  Age11_12,
  Age13_14,
  Age15_16,
  Age17_18,
  Age19_22
}

export const PitcherAgeBandLabel: Record<PitcherAgeBand, string> = {
  [PitcherAgeBand.Age7_8]: '7–8',
  [PitcherAgeBand.Age9_10]: '9–10',
  [PitcherAgeBand.Age11_12]: '11–12',
  [PitcherAgeBand.Age13_14]: '13–14',
  [PitcherAgeBand.Age15_16]: '15–16',
  [PitcherAgeBand.Age17_18]: '17–18',
  [PitcherAgeBand.Age19_22]: '19–22'
};

export const SOFTBALL_POSITIONS: readonly { value: number; label: string }[] = [
  { value: FieldPosition.Pitcher, label: FieldPositionLabel[FieldPosition.Pitcher] },
  { value: FieldPosition.Catcher, label: FieldPositionLabel[FieldPosition.Catcher] },
  { value: FieldPosition.FirstBase, label: FieldPositionLabel[FieldPosition.FirstBase] },
  { value: FieldPosition.SecondBase, label: FieldPositionLabel[FieldPosition.SecondBase] },
  { value: FieldPosition.ThirdBase, label: FieldPositionLabel[FieldPosition.ThirdBase] },
  { value: FieldPosition.Shortstop, label: FieldPositionLabel[FieldPosition.Shortstop] },
  { value: FieldPosition.LeftField, label: FieldPositionLabel[FieldPosition.LeftField] },
  { value: FieldPosition.CenterField, label: FieldPositionLabel[FieldPosition.CenterField] },
  { value: FieldPosition.RightField, label: FieldPositionLabel[FieldPosition.RightField] },
  { value: FieldPosition.DesignatedHitter, label: FieldPositionLabel[FieldPosition.DesignatedHitter] },
  { value: FieldPosition.ExtraHitter, label: FieldPositionLabel[FieldPosition.ExtraHitter] }
];
